/**
 * ⟦DEV · A-176 · C59⟧ **Le mode triche** (ex-télécommande de dev) : le panneau qui téléporte
 * Mathieu où il veut dans la partie.
 *
 * Demandé mot pour mot : *« un passcode ou un truc qui me permette de contrôler le nombre
 * d'abonnés que j'ai et où j'en suis dans le jeu, pour que je puisse faire des visites, me
 * réavancer »*. Les dix premières minutes étaient la seule chose testable ; tout ce qui vit
 * après 1 000 abonnés ne se voyait jamais. Puis, 03/10/2026 : *« il faut la rendre disponible en
 * mode cheat [...] pour la prod il faut un mode cheat avec un cheat code »*.
 *
 * **Où il vit** : monté à la racine (`App.tsx`), donc présent sur **tous** les écrans de la
 * partie, du build de production comme du développement, et **au-dessus de tout**, y compris du
 * mode découverte, qui fige le jeu (z-index 90). Un outil qu'on ne peut pas ouvrir quand on est
 * bloqué ne sert à rien.
 *
 * **Ce qu'il rend** : rien, tant que le mode n'est pas armé (`game/dev.ts` : URL `?triche`, code
 * Konami, sept tapes sur la télé MeTube) ; rien non plus en mode compte, où le serveur est
 * l'autorité. **Ce composant est donc aussi le seul écouteur de ces deux cheat codes** : il reste
 * monté et à l'écoute même quand il n'affiche rien.
 *
 * **Ce qu'il fait** : une triche, mais une triche **cohérente**. Toute la logique est dans
 * `game/dev.ts`, qui rejoue les pas 9-10 de la file de résolution après chaque retouche : on rend
 * des états que le moteur sait produire, jamais des champs modifiés au hasard. Et rien de tout ça
 * ne franchit la frontière du bac à sable : le serveur ne fait jamais confiance au client
 * (SPEC-API §6.4).
 *
 * **Le style est en `<style>` dans le composant, exprès** : il n'est injecté que quand le panneau
 * s'affiche, donc un joueur qui ne triche pas ne charge ni ne voit aucune de ces règles (un
 * `import "./dev.css"` les mettrait dans le CSS de tout le monde).
 *
 * Trois interdits respectés : aucun `window.confirm` / `alert` / `prompt` (CLAUDE.md), aucune
 * règle de jeu réécrite (tout vient de `@maxime/shared`), aucun chiffre recopié.
 */

import { useEffect, useState, useSyncExternalStore } from "react";

import {
  ENERGIE_MAX,
  OBJECTIF,
  PALIER_GRAND_SAUT,
  SURCHARGE_MAX,
  surchargeAccordee,
} from "@maxime/shared";

import {
  PALIERS_DEV,
  TRICHE_PAR_URL,
  armerTriche,
  avancerCode,
  avancerJours,
  compterTape,
  ecouterTriche,
  eteindreTriche,
  extinctionImminente,
  faillitteImminente,
  joursAvantExtinction,
  modeTricheActif,
  poserPalier,
  rearmerAnnonces,
  reglerAbonnes,
  reglerJauges,
  toutDeverrouiller,
  type Jauges,
} from "../game/dev";
import { ouvert } from "../game/escalier";
import { jourSimule, nombre, nombreCourt } from "../game/format";
import type { RunSnapshot } from "../game/gameClient";
import { interrupteurs } from "../game/menage";
import { bacASable, modeActif } from "../game/client";
import { useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { useSessionStore } from "../stores/sessionStore";
import { useUIStore } from "../stores/uiStore";

/** Un champ de saisie garde ses touches : on ne tape pas un cheat code dans un `<input>`. */
function cibleEditable(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) return false;
  return (
    cible.isContentEditable ||
    cible.tagName === "INPUT" ||
    cible.tagName === "TEXTAREA" ||
    cible.tagName === "SELECT"
  );
}

/**
 * Arme le mode triche (code Konami ou tapes) et le dit par un toast, jamais par `window.alert`.
 * ⟦A-176 verrou 2⟧ **En mode compte, rien ne se passe** : ni armement, ni toast.
 */
function armerParCode(): void {
  if (modeActif() !== "bac-a-sable") return;
  const { toast } = useUIStore.getState();
  if (!armerTriche()) {
    toast({ texte: "Mode triche déjà activé", ton: "info", icone: "🛠" });
    return;
  }
  // À l'écran-titre il n'y a pas encore de run : la poignée viendra avec la chambre.
  const enPartie = useRunStore.getState().run !== null;
  toast({
    texte: enPartie
      ? "Mode triche activé"
      : "Mode triche activé : la poignée 🛠 apparaît dans la chambre",
    ton: "info",
    icone: "🛠",
  });
}

export function PanneauDev() {
  // `useSyncExternalStore` : l'URL a déjà été lue (et rangée en `sessionStorage`) au premier
  // rendu, et le code Konami ou les tapes peuvent l'armer plus tard sans recharger la page.
  const arme = useSyncExternalStore(ecouterTriche, modeTricheActif, () => false);
  const statut = useSessionStore((s) => s.statut);
  const [deplie, setDeplie] = useState(false);
  const run = useRunStore((s) => s.run);
  const toast = useUIStore((s) => s.toast);
  const reinitialiserDecouverte = useDecouverteStore((s) => s.reinitialiser);

  const [abonnes, setAbonnes] = useState("0");
  const [jauges, setJauges] = useState<Required<Jauges>>(JAUGES_VIDES);
  const [jours, setJours] = useState("7");

  const state = run?.state ?? null;

  // ⟦A-176⟧ Les deux cheat codes, à l'écoute en permanence (c'est ce qui arme le panneau).
  // Clavier : le code Konami, en phase de capture pour qu'un écran qui garde ses flèches (un
  // mini-jeu) ne le prive pas des touches. Tactile : sept tapes rapides sur la télé MeTube ;
  // `pointerdown` plutôt que `click`, que Safari iOS ne remonte pas jusqu'au document depuis un
  // élément non cliquable.
  useEffect(() => {
    let tampon: string[] = [];
    let tapes: { n: number; t: number } | null = null;

    const auClavier = (e: KeyboardEvent) => {
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      if (typeof e.key !== "string" || cibleEditable(e.target)) return;
      const suite = avancerCode(tampon, e.key);
      tampon = suite.tampon;
      if (suite.complet) armerParCode();
    };
    const auToucher = (e: PointerEvent) => {
      if (!e.isPrimary || !(e.target instanceof Element)) return;
      if (!e.target.closest("[data-logo-metube]")) return;
      const suite = compterTape(tapes, e.timeStamp);
      tapes = suite.complet ? null : suite;
      if (suite.complet) armerParCode();
    };

    window.addEventListener("keydown", auClavier, true);
    document.addEventListener("pointerdown", auToucher, true);
    return () => {
      window.removeEventListener("keydown", auClavier, true);
      document.removeEventListener("pointerdown", auToucher, true);
    };
  }, []);

  // Les curseurs se resynchronisent quand le panneau s'ouvre — sinon ils affichent l'état
  // d'il y a dix minutes et « appliquer » remettrait le jeu en arrière sans le dire.
  useEffect(() => {
    if (!deplie || !state) return;
    setAbonnes(String(state.subscribers));
    setJauges({
      energy: Math.round(state.energy),
      surcharge: surchargeAccordee(state),
      mental: Math.round(state.mental),
      reputation: Math.round(state.reputation),
      loyalty: Math.round(state.loyalty),
      momentum: state.momentum,
      money: Math.round(state.money),
      pc: state.pc,
    });
  }, [deplie, state]);

  // ⟦A-176 verrou 2⟧ « Bac à sable uniquement. » Le module n'écrit que dans le store local ; sur
  // une run de compte, l'état vit en base et le serveur ne fait jamais confiance au client
  // (SPEC-API §6.4). Le mode triche n'a donc simplement pas de prise : on ne l'affiche pas plutôt
  // que d'offrir des boutons qui ne feraient rien. Pas non plus à l'écran-titre, où une run
  // gardée en mémoire (« Menu principal », ⟦A-141⟧) n'est pas la partie qu'on joue.
  if (!arme || statut !== "jeu" || !run || !state || modeActif() !== "bac-a-sable") return null;

  async function poser(suivante: RunSnapshot, message: string) {
    await bacASable.remplacerRun(suivante);
    useRunStore.setState({ run: suivante });
    toast({ texte: `🛠 ${message}`, ton: "info", icone: "🛠" });
  }

  const argentOuvert = ouvert(state, "argent");
  // ⟦C60 · A-177⟧ Les systèmes coupés par le grand ménage n'ont plus de bouton ici : le mode triche ne propose
  // rien qui ne fasse rien (« faillite imminente » quand la faillite n'existe plus, par exemple).
  const sys = interrupteurs();

  return (
    <>
      <style>{CSS_DEV}</style>

      <button
        type="button"
        className="dev-poignee"
        aria-expanded={deplie}
        title="Mode triche"
        aria-label="Mode triche"
        onClick={() => setDeplie((v) => !v)}
      >
        🛠
      </button>

      {deplie ? (
        <aside className="dev-tiroir" aria-label="Mode triche">
          <header className="dev-entete">
            <strong>🛠 Mode triche</strong>
            <span className="dev-note">bac à sable seulement</span>
            <button
              type="button"
              className="dev-fermer"
              aria-label="Fermer le mode triche"
              onClick={() => setDeplie(false)}
            >
              ✕
            </button>
          </header>

          <p className="dev-etat">
            {/* ⟦A-19⟧ `clock_h / 16` en dur ici : la télécommande annonçait J66 là où la
                chambre affichait J44. Le jour simulé a une seule définition, `jourSimule`. */}
            {nombre(state.subscribers)} abonnés · {state.counters.videos_published} publication(s)
            · J{jourSimule(state.clock_h)} ·{" "}
            {argentOuvert ? `${nombre(state.money)} ₵` : "Clicks verrouillés"}
          </p>

          {/* ── 1. Se poser à un palier ─────────────────────────────────────────── */}
          <section className="dev-bloc">
            <h2>Se poser à un palier</h2>
            <div className="dev-grille">
              {PALIERS_DEV.map((palier) => (
                <button
                  key={palier}
                  type="button"
                  className="dev-bouton"
                  onClick={() =>
                    void poser(
                      poserPalier(run, palier),
                      `posé à ${nombreCourt(palier)} abonnés — escalier ouvert`,
                    )
                  }
                >
                  {nombreCourt(palier)}
                </button>
              ))}
            </div>
            <p className="dev-aide">
              Abonnés + les publications qu'il faut pour ouvrir l'escalier, puis le moteur
              recolle : paliers franchis, points, et le Grand Saut à 1 000. Au
              million, la victoire se grave à ta prochaine action — c'est le moteur qui la pose,
              pas ce panneau.
            </p>
          </section>

          {/* ── 2. Abonnés à la valeur libre ─────────────────────────────────────── */}
          <section className="dev-bloc">
            <h2>Abonnés, valeur libre</h2>
            <div className="dev-ligne">
              <input
                type="number"
                min={0}
                max={OBJECTIF * 10}
                value={abonnes}
                aria-label="Nombre d'abonnés"
                onChange={(e) => setAbonnes(e.target.value)}
              />
              <button
                type="button"
                className="dev-bouton"
                onClick={() =>
                  void poser(
                    reglerAbonnes(run, Number(abonnes) || 0),
                    `abonnés → ${nombre(Number(abonnes) || 0)}`,
                  )
                }
              >
                Poser
              </button>
            </div>
            <p className="dev-aide">
              Ne touche à rien d'autre — les paliers déjà franchis restent franchis (ils ne se
              reprennent jamais, PLAN §4.13).
            </p>
          </section>

          {/* ── 3. Les jauges ────────────────────────────────────────────────────── */}
          <section className="dev-bloc">
            <h2>Les jauges</h2>
            {/* ⟦A-20⟧ Le curseur d'énergie monte jusqu'au plafond ATTEIGNABLE, pas jusqu'à 100 :
                la surcharge se règle juste en dessous, et sans elle on ne pouvait pas mettre le
                jeu dans l'état qu'on veut regarder. Les deux bornes viennent du moteur. */}
            <Curseur
              nom="Énergie"
              max={ENERGIE_MAX + SURCHARGE_MAX}
              valeur={jauges.energy}
              onChange={(v) => setJauges({ ...jauges, energy: v })}
            />
            <Curseur
              nom="Surcharge"
              max={SURCHARGE_MAX}
              valeur={jauges.surcharge}
              affichage={jauges.surcharge > 0 ? `+${jauges.surcharge}` : "—"}
              onChange={(v) => setJauges({ ...jauges, surcharge: v })}
            />
            <p className="dev-aide">
              La surcharge ouvre le plafond d'énergie au-dessus de {ENERGIE_MAX} (jusqu'à{" "}
              {ENERGIE_MAX + SURCHARGE_MAX}) ; l'énergie le remplit. En jouant, ça s'obtient par la
              grasse matinée — deux nuits d'affilée — ou par une boisson.
            </p>
            <Curseur
              nom="Santé mentale"
              valeur={jauges.mental}
              onChange={(v) => setJauges({ ...jauges, mental: v })}
            />
            {/* ⟦C60 bis · A-177⟧ La réputation a quitté l'écran (arbitrage n° 2) : plus de curseur. Rallumée, il revient. */}
            {sys.reputation ? (
              <Curseur
                nom="Réputation"
                valeur={jauges.reputation}
                onChange={(v) => setJauges({ ...jauges, reputation: v })}
              />
            ) : null}
            <Curseur
              nom="Fidélité"
              valeur={jauges.loyalty}
              onChange={(v) => setJauges({ ...jauges, loyalty: v })}
            />
            <Curseur
              nom="Momentum"
              min={0.8}
              max={1.5}
              pas={0.01}
              valeur={jauges.momentum}
              affichage={`×${jauges.momentum.toFixed(2).replace(".", ",")}`}
              onChange={(v) => setJauges({ ...jauges, momentum: v })}
            />
            <div className="dev-ligne">
              <label className="dev-champ">
                <span>Clicks</span>
                <input
                  type="number"
                  value={jauges.money}
                  disabled={!argentOuvert}
                  onChange={(e) => setJauges({ ...jauges, money: Number(e.target.value) })}
                />
              </label>
              <label className="dev-champ">
                <span>Points</span>
                <input
                  type="number"
                  min={0}
                  value={jauges.pc}
                  onChange={(e) => setJauges({ ...jauges, pc: Number(e.target.value) })}
                />
              </label>
            </div>
            {!argentOuvert ? (
              <p className="dev-aide">
                Les Clicks n'existent pas avant le Grand Saut ⟦A-1⟧ : pose-toi à 1 000 abonnés
                d'abord, sinon on aurait un solde sans compteur de Clicks.
              </p>
            ) : null}
            <div className="dev-grille">
              <button
                type="button"
                className="dev-bouton"
                onClick={() => void poser(reglerJauges(run, jauges), "jauges appliquées")}
              >
                Appliquer
              </button>
              <button
                type="button"
                className="dev-bouton"
                onClick={() =>
                  void poser(
                    reglerJauges(run, {
                      energy: ENERGIE_MAX,
                      surcharge: 0,
                      mental: 100,
                      // ⟦C60 bis⟧ Éteinte, la jauge n'est plus proposée : les préréglages n'y touchent plus.
                      ...(sys.reputation ? { reputation: 100 } : {}),
                      loyalty: 100,
                      momentum: 1.5,
                    }),
                    "tout au vert",
                  )
                }
              >
                Tout au vert
              </button>
              <button
                type="button"
                className="dev-bouton danger"
                onClick={() =>
                  void poser(
                    reglerJauges(run, {
                      energy: 8,
                      surcharge: 0,
                      mental: 12,
                      ...(sys.reputation ? { reputation: 18 } : {}),
                      loyalty: 20,
                    }),
                    "zone rouge",
                  )
                }
              >
                Zone rouge
              </button>
              {sys.faillite ? (
                <button
                  type="button"
                  className="dev-bouton danger"
                  disabled={!argentOuvert}
                  title={
                    argentOuvert
                      ? "Compte à rebours de la faillite à J−7"
                      : "Impossible avant le Grand Saut : sans couche argent, pas de faillite"
                  }
                  onClick={() => void poser(faillitteImminente(run, 7), "faillite dans 7 jours")}
                >
                  Faillite J−7
                </button>
              ) : null}
              {/* ⟦A-58⟧ §9.4bis « Le Fantôme ». Les gardes s'arment ici ; l'horloge, elle, est
                  la rente — d'où le nombre de jours affiché, qui dit ce qu'il reste à laisser
                  couler (« +N jours » juste au-dessus). */}
              <button
                type="button"
                className="dev-bouton danger"
                title={
                  "Arme les deux gardes de l'extinction (5 nulles + momentum au plancher). " +
                  "L'horloge est la rente : avance les jours si elle n'est pas encore à zéro."
                }
                onClick={() => void poser(extinctionImminente(run), "extinction armée")}
              >
                Fantôme
                {joursAvantExtinction(run.state) > 0
                  ? ` (encore ${joursAvantExtinction(run.state)} j)`
                  : " (prêt)"}
              </button>
            </div>
          </section>

          {/* ── 4. Déverrouillage et pédagogie ───────────────────────────────────── */}
          <section className="dev-bloc">
            <h2>Déverrouiller</h2>
            <div className="dev-grille">
              <button
                type="button"
                className="dev-bouton"
                onClick={() => void poser(toutDeverrouiller(run), "tout déverrouillé")}
              >
                Tout ouvrir
              </button>
              <button
                type="button"
                className="dev-bouton"
                onClick={() =>
                  void poser(rearmerAnnonces(run), "annonces d'escalier réarmées")
                }
              >
                Rejouer les annonces
              </button>
              <button
                type="button"
                className="dev-bouton"
                onClick={() => {
                  reinitialiserDecouverte();
                  toast({ texte: "🛠 mode découverte rejoué depuis zéro", ton: "info", icone: "🛠" });
                }}
              >
                Rejouer la découverte
              </button>
            </div>
            <p className="dev-aide">
              « Tout ouvrir » te pose à {nombre(PALIER_GRAND_SAUT)} abonnés si tu es en dessous :
              le dernier étage, les Clicks, EST le Grand Saut, et il est attaché à ce palier
              ⟦A-11 §4⟧.
            </p>
          </section>

          {/* ── 5. Le temps ──────────────────────────────────────────────────────── */}
          <section className="dev-bloc">
            <h2>Avancer le temps</h2>
            <div className="dev-grille">
              {[1, 7, 30].map((n) => (
                <button
                  key={n}
                  type="button"
                  className="dev-bouton"
                  onClick={() => void poser(avancerJours(run, n), `+${n} jour(s)`)}
                >
                  +{n} j
                </button>
              ))}
            </div>
            <div className="dev-ligne">
              <input
                type="number"
                min={1}
                max={365}
                value={jours}
                aria-label="Jours à avancer"
                onChange={(e) => setJours(e.target.value)}
              />
              <button
                type="button"
                className="dev-bouton"
                onClick={() =>
                  void poser(avancerJours(run, Number(jours) || 1), `+${Number(jours) || 1} jour(s)`)
                }
              >
                Avancer
              </button>
            </div>
            <p className="dev-aide">
              Une journée = les tranches de courbe versées{sys.charges ? ", les charges débitées" : ""}, puis
              la nuit (rente des anciennes vidéos, désabonnements, momentum, tendances). Tout est
              joué par le moteur, dans son ordre.
            </p>
          </section>

          <footer className="dev-pied">
            <button
              type="button"
              className="dev-bouton danger"
              onClick={() => {
                setDeplie(false);
                eteindreTriche();
                toast({ texte: "Mode triche éteint", ton: "info", icone: "🛠" });
              }}
            >
              Éteindre le mode triche
            </button>
            <p className="dev-aide">
              {TRICHE_PAR_URL ? (
                <>
                  <code>?triche=off</code> l'éteint aussi, et fermer l'onglet.
                </>
              ) : (
                <>Fermer l'onglet l'éteint aussi.</>
              )}
              {import.meta.env.DEV ? (
                <>
                  {" "}
                  <code>?devScore=82</code> traverse l'Atelier sans jouer.
                </>
              ) : null}
            </p>
          </footer>
        </aside>
      ) : null}
    </>
  );
}

const JAUGES_VIDES: Required<Jauges> = {
  energy: ENERGIE_MAX,
  surcharge: 0,
  mental: 100,
  reputation: 50,
  loyalty: 50,
  momentum: 1,
  money: 0,
  pc: 0,
};

function Curseur({
  nom,
  valeur,
  onChange,
  min = 0,
  max = 100,
  pas = 1,
  affichage,
}: {
  nom: string;
  valeur: number;
  onChange(v: number): void;
  min?: number;
  max?: number;
  pas?: number;
  affichage?: string;
}) {
  return (
    <label className="dev-curseur">
      <span className="dev-curseur-nom">{nom}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={pas}
        value={valeur}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className="dev-curseur-valeur">{affichage ?? Math.round(valeur)}</span>
    </label>
  );
}

/** Volontairement sobre : c'est un outil, il ne doit pas se faire passer pour du jeu. */
const CSS_DEV = `
.dev-poignee {
  position: fixed; right: 14px; bottom: 14px; z-index: 120;
  width: 42px; height: 42px; border-radius: 50%;
  border: var(--trait) solid var(--marqueur); background: var(--kraft-clair);
  font-size: 20px; line-height: 1; cursor: pointer; box-shadow: var(--ombre-carton);
}
.dev-poignee:hover { background: var(--kraft); }
/* Les toasts (z-index 70) montent au-dessus de la poignée (120), sinon elle les recouvre. */
#app .toasts { right: 14px; bottom: 70px; }
.dev-tiroir {
  position: fixed; right: 14px; bottom: 66px; z-index: 119;
  width: min(340px, calc(100vw - 28px)); max-height: min(78vh, 760px); overflow-y: auto;
  background: var(--papier); color: var(--text);
  border: var(--trait) solid var(--marqueur); border-radius: var(--r-3);
  box-shadow: var(--ombre-carton); padding: 10px 12px 12px;
  font-family: var(--police); font-size: 12px;
}
.dev-entete { display: flex; align-items: baseline; gap: 8px; }
.dev-entete strong { font-size: 13px; }
.dev-note { color: var(--muted); font-size: 10px; flex: 1; }
.dev-fermer { border: 0; background: none; cursor: pointer; font-size: 14px; color: var(--muted); }
.dev-etat {
  margin: 6px 0 10px; padding: 5px 7px; border-radius: var(--r-2);
  background: var(--papier-creuse); font-family: var(--police-chiffres); font-size: 11px;
}
.dev-bloc { border-top: var(--trait-fin) solid var(--papier-ligne); padding-top: 8px; margin-top: 8px; }
.dev-bloc h2 { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--muted); margin: 0 0 6px; }
.dev-grille { display: flex; flex-wrap: wrap; gap: 5px; }
.dev-bouton {
  flex: 1 1 auto; min-width: 62px; padding: 5px 8px; cursor: pointer;
  border: var(--trait-fin) solid var(--marqueur); border-radius: var(--r-2);
  background: var(--papier-creuse); color: var(--text); font: inherit; font-size: 11px;
}
.dev-bouton:hover:not(:disabled) { background: var(--kraft-clair); }
.dev-bouton:disabled { opacity: 0.45; cursor: not-allowed; }
.dev-bouton.danger { border-color: var(--danger); color: var(--danger); }
.dev-ligne { display: flex; gap: 6px; align-items: flex-end; margin: 6px 0; }
.dev-ligne input[type="number"] {
  flex: 1; min-width: 0; padding: 4px 6px; font: inherit;
  font-family: var(--police-chiffres);
  border: var(--trait-fin) solid var(--marqueur); border-radius: var(--r-2); background: var(--papier);
}
.dev-champ { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.dev-champ span { font-size: 10px; color: var(--muted); }
.dev-champ input {
  padding: 4px 6px; font: inherit; font-family: var(--police-chiffres);
  border: var(--trait-fin) solid var(--marqueur); border-radius: var(--r-2); background: var(--papier);
}
.dev-champ input:disabled { opacity: 0.45; }
.dev-curseur { display: grid; grid-template-columns: 88px 1fr 40px; align-items: center; gap: 6px; margin: 3px 0; }
.dev-curseur-nom { font-size: 11px; }
.dev-curseur input { width: 100%; }
.dev-curseur-valeur { font-family: var(--police-chiffres); font-size: 11px; text-align: right; }
.dev-aide { color: var(--muted); font-size: 10px; line-height: 1.35; margin: 6px 0 0; }
.dev-pied { color: var(--muted); font-size: 10px; margin-top: 10px; }
.dev-pied .dev-bouton { width: 100%; }
.dev-pied code { font-family: var(--police-chiffres); }
`;
